(() => {
  const endpoint = window.SparkProofs?.intakeEndpoint || 'https://configurator.sparkstickers.com/api/proofs/intake-upload';
  const MAX_UPLOAD_FILES = 20;
  const MAX_UPLOAD_BYTES = 50 * 1024 * 1024;

  function initForms() {
    document.querySelectorAll('[data-spark-proof-form]').forEach((form) => {
      if (form.dataset.sparkProofReady === 'true') return;
      form.dataset.sparkProofReady = 'true';

      const fileInput = form.querySelector('[data-spark-proof-file]');
      const artworkLink = form.querySelector('[data-spark-artwork-link]');
      const drop = form.querySelector('.spark-proof-drop');
      const dropCopy = form.querySelector('.spark-proof-drop__copy');
      const status = form.querySelector('[data-spark-proof-status]');
      const completion = form.querySelector('[data-spark-proof-completion]');
      const submit = form.querySelector('[data-spark-proof-submit]');
      const groups = form.querySelectorAll('[data-spark-group]');
      const options = form.querySelectorAll('[data-spark-request-option]');
      const artworkModes = form.querySelectorAll('[data-spark-artwork-mode]');
      const artworkPanels = form.querySelectorAll('[data-spark-artwork-panel]');
      const additionalDetails = form.querySelector('[data-spark-additional-details]');
      let progress;

      function selectedType() {
        return form.querySelector('[data-spark-request-option]:checked')?.dataset.sparkRequestOption || 'quote';
      }

      function selectedArtworkMode() {
        return form.querySelector('[data-spark-artwork-mode]:checked')?.dataset.sparkArtworkMode || 'upload';
      }

      function toggleFields() {
        const type = selectedType();

        groups.forEach((group) => {
          const show = (group.dataset.sparkGroup || '').split(/\s+/).includes(type);
          group.hidden = !show;
          group.querySelectorAll('input, select, textarea').forEach((field) => {
            field.disabled = !show;
            field.required = show && field.dataset.required === 'true';
          });
        });

        const placeholderSource = form.querySelector(`[data-spark-details-placeholder="${type}"]`);
        if (additionalDetails && placeholderSource?.dataset.placeholder) {
          additionalDetails.placeholder = placeholderSource.dataset.placeholder;
        }

        submit.textContent = submit.dataset[`label${type.charAt(0).toUpperCase()}${type.slice(1)}`] || 'Submit';
      }

      function toggleArtworkMode() {
        const mode = selectedArtworkMode();
        artworkPanels.forEach((panel) => {
          const show = panel.dataset.sparkArtworkPanel === mode;
          panel.hidden = !show;
          panel.querySelectorAll('input').forEach((input) => {
            input.disabled = !show;
          });
        });
        if (fileInput) fileInput.required = mode === 'upload';
        if (artworkLink) artworkLink.required = mode === 'link';
        setStatus('');
      }

      function setStatus(message, isError = false) {
        status.textContent = message;
        status.hidden = !message;
        status.classList.toggle('spark-form-note--error', isError);
      }

      function showCompletion() {
        form.style.minHeight = `${Math.ceil(form.getBoundingClientRect().height)}px`;
        setStatus('');
        form.classList.add('is-complete');
        completion.hidden = false;
      }

      function resetCompletion() {
        form.classList.remove('is-complete');
        form.style.minHeight = '';
        completion.hidden = true;
        setStatus('');
      }

      function setProgress(percent) {
        if (!progress) {
          progress = document.createElement('span');
          const wrap = document.createElement('span');
          wrap.className = 'spark-proof-progress';
          wrap.append(progress);
          dropCopy.append(wrap);
        }
        progress.parentElement.style.setProperty('--spark-proof-progress', `${percent}%`);
      }

      function showFileName() {
        const files = Array.from(fileInput.files || []);
        const existing = dropCopy.querySelector('.spark-proof-file-name');
        if (existing) existing.remove();
        if (!files.length) return;
        const fileName = document.createElement('strong');
        fileName.className = 'spark-proof-file-name';
        fileName.textContent = files.length === 1 ? files[0].name : `${files.length} files selected`;
        dropCopy.prepend(fileName);
        setProgress(0);
      }

      options.forEach((option) => option.addEventListener('change', toggleFields));
      artworkModes.forEach((option) => option.addEventListener('change', toggleArtworkMode));
      fileInput.addEventListener('change', showFileName);
      drop.addEventListener('dragenter', () => drop.classList.add('is-dragover'));
      drop.addEventListener('dragleave', () => drop.classList.remove('is-dragover'));
      drop.addEventListener('drop', () => drop.classList.remove('is-dragover'));

      const deliveryDate = form.querySelector('[data-spark-delivery-date]');
      if (deliveryDate) deliveryDate.min = new Date().toISOString().slice(0, 10);

      const requestedType = new URLSearchParams(window.location.search).get('mode');
      const requestedOption = ['quote', 'precheck', 'order'].includes(requestedType)
        ? form.querySelector(`[data-spark-request-option="${requestedType}"]`)
        : null;
      if (requestedOption) requestedOption.checked = true;

      toggleFields();
      toggleArtworkMode();

      window.addEventListener('pageshow', (event) => {
        if (event.persisted) resetCompletion();
      });

      form.addEventListener('submit', async (event) => {
        event.preventDefault();
        const mode = selectedArtworkMode();
        const files = Array.from(fileInput.files || []);
        const link = artworkLink.value.trim();
        if (mode === 'upload' && !files.length) {
          setStatus('Please choose at least one artwork file before submitting.', true);
          return;
        }
        if (mode === 'upload' && files.length > MAX_UPLOAD_FILES) {
          setStatus(`Please choose no more than ${MAX_UPLOAD_FILES} artwork files at a time.`, true);
          return;
        }
        if (mode === 'upload' && files.reduce((total, file) => total + file.size, 0) > MAX_UPLOAD_BYTES) {
          setStatus('The selected files exceed the 50 MB combined upload limit.', true);
          return;
        }
        if (mode === 'link' && !isSecureArtworkLink(link)) {
          setStatus('Please paste a valid HTTPS artwork link before submitting.', true);
          return;
        }

        submit.disabled = true;
        setStatus(mode === 'upload' ? 'Uploading artwork...' : 'Sending artwork link...');

        try {
          const submittedType = selectedType();
          const payload = formPayload(form, submittedType, files);
          if (files.length) setProgress(30);
          const intakeResponse = await fetch(endpoint, {
            method: 'POST',
            headers: { Accept: 'application/json' },
            body: payload,
          });
          const intake = await intakeResponse.json();
          if (!intakeResponse.ok) throw new Error(intake.error || 'Artwork request failed.');

          if (files.length) setProgress(100);
          form.reset();
          const submittedOption = form.querySelector(`[data-spark-request-option="${submittedType}"]`);
          if (submittedOption) submittedOption.checked = true;
          toggleFields();
          toggleArtworkMode();
          showFileName();
          showCompletion();
        } catch (error) {
          setStatus(error.message || 'Artwork request failed.', true);
        } finally {
          submit.disabled = false;
        }
      });
    });
  }

  function formPayload(form, requestType, files = []) {
    const data = new FormData(form);
    data.set('requestType', requestType);
    data.delete('artwork');
    if (files.length) {
      files.forEach((file, index) => data.append(`artwork_${index + 1}`, file, file.name));
      data.delete('artworkLink');
    }
    return data;
  }

  function isSecureArtworkLink(value) {
    try {
      return new URL(value).protocol === 'https:';
    } catch {
      return false;
    }
  }

  initForms();
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initForms, { once: true });
  }
})();
